<template>
  <div class="sp-countdown" :class="classModifiers">
    <span class="sp-countdown__label">{{ label }}</span>
    <!-- No whitespace between the spans, so nothing adds space between the digits and the colons. -->
    <!-- prettier-ignore -->
    <span class="sp-countdown__clock"><span class="sp-countdown__unit">{{ days }}<span class="sp-countdown__unit-label">Days</span></span>:<span class="sp-countdown__unit">{{ hours }}<span class="sp-countdown__unit-label">Hours</span></span>:<span class="sp-countdown__unit">{{ minutes }}<span class="sp-countdown__unit-label">Min<span>utes</span></span></span>:<span class="sp-countdown__unit">{{ seconds }}<span class="sp-countdown__unit-label">Sec<span>onds</span></span></span></span>
  </div>
</template>

<script setup>
import { props as countdownProps } from "./props.js";
import { useNow, watchImmediate } from "@vueuse/core";
import { computed, ref } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 60 * SECONDS_PER_MINUTE;
const SECONDS_PER_DAY = 24 * SECONDS_PER_HOUR;

const props = defineProps(countdownProps);

const { breakpointModifiers } = useBreakpoints();
const classModifiers = computed(() => [...breakpointModifiers.value]);

function sanitizeTimezone(timezone) {
  if (!timezone) {
    return "";
  }

  if (timezone.includes(":")) {
    return timezone;
  }

  return `${timezone.slice(0, 3)}:${timezone.slice(3)}`;
}

function parseRailsDateTime(dateString) {
  const parts = dateString.trim().split(/\s+/);

  if (parts.length < 3) {
    return new Date(NaN);
  }

  const [date, time, tz] = parts;

  const timezone = sanitizeTimezone(tz);

  return new Date(`${date}T${time}${timezone}`);
}

const nowDate = useNow();
const now = computed(() => nowDate.value.getTime());
const countdownDate = ref(null);

const remainingSeconds = computed(() => {
  if (!countdownDate.value) {
    return 0;
  }

  const timeDifference = Math.floor((countdownDate.value - now.value) / 1000);
  return Math.max(0, timeDifference);
});

function initializeCountdown() {
  const parsedDate = parseRailsDateTime(props.date);

  if (isNaN(parsedDate.getTime())) {
    countdownDate.value = null;
    return;
  }

  countdownDate.value = parsedDate.getTime();
}

function formatTimeUnit(value) {
  return Math.max(0, value).toString().padStart(2, "0");
}

watchImmediate(() => props.date, initializeCountdown);

const days = computed(() => Math.max(0, Math.floor(remainingSeconds.value / SECONDS_PER_DAY)));

const hours = computed(() => formatTimeUnit(Math.floor((remainingSeconds.value % SECONDS_PER_DAY) / SECONDS_PER_HOUR)));

const minutes = computed(() =>
  formatTimeUnit(Math.floor((remainingSeconds.value % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE)),
);

const seconds = computed(() => formatTimeUnit(remainingSeconds.value % SECONDS_PER_MINUTE));
</script>

<style>
:host {
  display: block;
}
</style>

<style scoped lang="scss">
.sp-countdown {
  --countdown-font: var(--sp-sys-typescale-body-bold-short, inherit);
  --countdown-label-font-size: var(--sp-ref-scale-40, 2.5rem);
  --countdown-label-font-size-mobile: var(--sp-ref-scale-32, 2rem);
  --countdown-clock-font-size: var(--sp-ref-scale-64, 4rem);
  --countdown-clock-font-size-mobile: var(--sp-ref-scale-32, 2rem);
  --countdown-clock-label-font-size: var(--sp-ref-scale-20, 1.25rem);
  --countdown-clock-label-font-size-mobile: var(--sp-ref-scale-16, 1rem);
  --countdown-gap: var(--sp-ref-scale-6, 0.375rem);
  /* Pulls the unit labels up into the clock's line spacing, toward the digits. */
  --countdown-clock-label-shift: calc(var(--countdown-clock-font-size) * 0.125);

  /* Stacked in column, until other variants are designed */
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--countdown-gap);

  font: var(--countdown-font);

  &.--is-sm-screen {
    --countdown-clock-label-shift: calc(var(--countdown-clock-font-size-mobile) * 0.125);

    .sp-countdown__label {
      font-size: var(--countdown-label-font-size-mobile);
    }

    .sp-countdown__clock {
      font-size: var(--countdown-clock-font-size-mobile);
    }

    .sp-countdown__unit-label,
    .sp-countdown__clock::after {
      font-size: var(--countdown-clock-label-font-size-mobile);
    }

    /* Hide selected text and add dot */
    .sp-countdown__unit-label span {
      display: none;
    }

    .sp-countdown__unit-label:has(span)::after {
      content: ".";
    }
  }
}

.sp-countdown__label {
  font-size: var(--countdown-label-font-size);
}

.sp-countdown__clock {
  font-size: var(--countdown-clock-font-size);

  /* Reserves one line of label height below the digits, which the out-of-flow labels hang into. */
  &::after {
    content: "\00a0";
    display: block;
    margin-block-start: calc(var(--countdown-clock-label-shift) * -1);
    font-size: var(--countdown-clock-label-font-size);
  }
}

/* An inline-block spans the full line height, which the label is positioned from. */
.sp-countdown__unit {
  display: inline-block;
  position: relative;
}

.sp-countdown__unit-label {
  position: absolute;
  top: calc(100% - var(--countdown-clock-label-shift));
  left: 50%;
  translate: -50%;
  font-size: var(--countdown-clock-label-font-size);
  white-space: nowrap;
}
</style>
